import {Component, ErrorInfo, ReactNode} from 'react'
import {Alert, Button, Container} from 'react-bootstrap'
import {ArrowClockwise, ExclamationTriangle, House} from 'react-bootstrap-icons'
import {analyticsService} from '../services/analyticsService'

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorCount: number;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    }
  }

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return {hasError: true}
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo)

    analyticsService.track('system.error.unhandled', {
      error_message: error.message,
      error_name: error.name
    })

    const isChunkError =
      error.message?.includes('dispatcher is null') ||
      error.message?.includes('dynamically imported module') ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Loading CSS chunk')

    if (isChunkError) {
      const reloadKey = 'chunk-error-reload'
      const lastReload = sessionStorage.getItem(reloadKey)
      const now = Date.now()

      if (!lastReload || now - parseInt(lastReload) > 10000) {
        sessionStorage.setItem(reloadKey, now.toString())
        window.location.reload()
        return
      }
    }

    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1
    }))

    if (import.meta.env.PROD) {
      // Example: logErrorToService(error, errorInfo)
    }
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    })
  }

  handleGoHome = (): void => {
    window.location.href = '/'
  }

  render(): ReactNode {
    if (this.state.hasError) {
      const isDevelopment = import.meta.env.DEV

      return (
        <Container className="min-vh-100 d-flex align-items-center justify-content-center">
          <div className="text-center" style={{maxWidth: '600px'}}>
            <ExclamationTriangle
              size={64}
              className="text-warning mb-4"
            />

            <h1 className="h3 mb-3">Oops! Something went wrong</h1>

            <p className="text-muted mb-4">
              We&apos;re sorry, but something unexpected happened.
              The error has been logged and we&apos;ll look into it.
            </p>

            {isDevelopment && this.state.error && (
              <Alert variant="danger" className="text-start mb-4">
                <Alert.Heading className="h6">
                  Error Details (Development Mode)
                </Alert.Heading>
                <hr/>
                <div className="small">
                  <strong>Error:</strong> {this.state.error.toString()}
                </div>
                {this.state.errorInfo && (
                  <details className="mt-3">
                    <summary className="cursor-pointer">Component Stack</summary>
                    <pre className="mt-2 p-2 bg-light rounded small text-start overflow-auto">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </Alert>
            )}

            <div className="d-flex gap-3 justify-content-center">
              <Button
                variant="primary"
                onClick={this.handleReset}
                className="d-flex align-items-center gap-2"
              >
                <ArrowClockwise size={20}/>
                Try Again
              </Button>

              <Button
                variant="outline-secondary"
                onClick={this.handleGoHome}
                className="d-flex align-items-center gap-2"
              >
                <House size={20}/>
                Go to Home
              </Button>
            </div>

            {this.state.errorCount > 2 && (
              <Alert variant="info" className="mt-4">
                <small>
                  This error has occurred multiple times.
                  Please try refreshing the page or clearing your browser cache.
                </small>
              </Alert>
            )}
          </div>
        </Container>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
